// File: src/pages/organizations/SubscriptionSuccess.js
// Route: /organization/:orgId/subscription/success

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { db, functions } from '../../firebase';
import { httpsCallable } from 'firebase/functions';
import { useAuth } from '../../contexts/authContext';

const SubscriptionSuccess = () => {
  const { orgId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const auth = useAuth();
  const currentUser = auth?.currentUser;
  const authLoading = auth?.authLoading ?? false;
  const [organization, setOrganization] = useState(null);
  const [checkoutSession, setCheckoutSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    // After Stripe redirect, auth might be temporarily null while re-initializing
    // If we have a session_id, that's proof payment completed - proceed regardless of auth state
    // The session_id is the key - if it exists, we can show the success page
    
    if (sessionId) {
      // We have a session ID - payment was completed, proceed to show success page
      // Don't wait for auth - the session_id is proof of payment
      loadOrganization();
      loadCheckoutSession();
    } else {
      // No session ID - wait for auth to finish loading, then check
      if (!authLoading) {
        if (!currentUser) {
          // No session ID and no user - show error
          setError('No checkout session ID found. Please contact support if you completed a payment.');
        } else {
          // User is logged in but no session ID - might have navigated here directly
          loadOrganization();
          setError('No checkout session ID found. Please contact support if you completed a payment.');
        }
        setLoading(false);
      }
    }
  }, [orgId, sessionId, currentUser, authLoading, navigate]);

  const loadOrganization = async () => {
    try {
      // Wait briefly for auth to re-initialize after Stripe redirect
      await new Promise((resolve) => setTimeout(resolve, 1500));
      const orgDoc = await getDoc(doc(db, 'organizations', orgId));
      if (orgDoc.exists()) {
        setOrganization({ id: orgDoc.id, ...orgDoc.data() });
      }
    } catch (error) {
      console.error('Error loading organization:', error);
      // If Firestore fails due to permissions, we'll use checkout session data instead
    }
  };

  const loadCheckoutSession = async () => {
    try {
      setLoading(true);
      setError('');
      
      // Call Cloud Function to retrieve checkout session details
      const getCheckoutSession = httpsCallable(functions, 'getCheckoutSession');
      const result = await getCheckoutSession({ sessionId });
      
      if (result.data) {
        setCheckoutSession(result.data);
      } else {
        setError('Could not retrieve checkout session details.');
      }
    } catch (error) {
      console.error('Error loading checkout session:', error);
      setError('Failed to load checkout session details. Your payment was successful, but we could not retrieve the receipt details.');
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadReceipt = async () => {
    if (!checkoutSession?.invoiceId) {
      alert('Receipt is not available yet. It will be emailed to you shortly.');
      return;
    }

    try {
      // Call Cloud Function to get invoice PDF
      const getInvoicePDF = httpsCallable(functions, 'getInvoicePDF');
      const result = await getInvoicePDF({ invoiceId: checkoutSession.invoiceId });
      
      if (result.data?.url) {
        // Open PDF in new tab
        window.open(result.data.url, '_blank');
      } else {
        alert('Receipt download is not available yet. It will be emailed to you shortly.');
      }
    } catch (error) {
      console.error('Error downloading receipt:', error);
      alert('Could not download receipt. It will be emailed to you shortly.');
    }
  };

  const handleSetupCommunity = () => {
    // Navigate to organization management page
    navigate(`/organization/${orgId}`);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading your subscription details...</p>
        </div>
      </div>
    );
  }

  if (error && !checkoutSession) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8 text-center">
          <div className="text-red-500 text-5xl mb-4">⚠️</div>
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Error</h1>
          <p className="text-gray-600 mb-6">{error}</p>
          <button
            onClick={() => navigate(`/organization/${orgId}/subscription`)}
            className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition"
          >
            Go to Subscription Page
          </button>
        </div>
      </div>
    );
  }

  const customerEmail = checkoutSession?.customerEmail || currentUser?.email || 'your email address';
  const amountTotal = checkoutSession?.amountTotal ? (checkoutSession.amountTotal / 100).toFixed(2) : null;
  const currency = checkoutSession?.currency?.toUpperCase() || 'USD';

  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4">
      <div className="max-w-2xl mx-auto">
        {/* Success Header */}
        <div className="bg-white rounded-lg shadow-lg p-8 mb-6 text-center">
          <div className="text-green-500 text-6xl mb-4">✓</div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Thank You!</h1>
          <p className="text-lg text-gray-600">
            Your subscription has been successfully activated.
          </p>
        </div>

        {/* Subscription Details */}
        {checkoutSession && (
          <div className="bg-white rounded-lg shadow-lg p-8 mb-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Subscription Details</h2>
            <div className="space-y-3">
              {(organization?.name || checkoutSession?.organizationName) && (
                <div className="flex justify-between">
                  <span className="text-gray-600">Organization:</span>
                  <span className="font-medium text-gray-900">
                    {organization?.name || checkoutSession?.organizationName}
                  </span>
                </div>
              )}
              {amountTotal && (
                <div className="flex justify-between">
                  <span className="text-gray-600">Amount Paid:</span>
                  <span className="font-medium text-gray-900">
                    {currency} ${amountTotal}
                  </span>
                </div>
              )}
              {checkoutSession.subscriptionId && (
                <div className="flex justify-between">
                  <span className="text-gray-600">Subscription ID:</span>
                  <span className="font-mono text-sm text-gray-600">
                    {typeof checkoutSession.subscriptionId === 'string'
                      ? `${checkoutSession.subscriptionId.substring(0, 20)}...`
                      : checkoutSession.subscriptionId?.id
                        ? `${checkoutSession.subscriptionId.id.substring(0, 20)}...`
                        : 'Processing...'}
                  </span>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Receipt Section */}
        <div className="bg-white rounded-lg shadow-lg p-8 mb-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Receipt</h2>
          <div className="space-y-4">
            <p className="text-gray-600">
              A receipt has been sent to <strong>{customerEmail}</strong>
            </p>
            <button
              onClick={handleDownloadReceipt}
              className="w-full bg-gray-100 text-gray-700 px-6 py-3 rounded-lg hover:bg-gray-200 transition flex items-center justify-center gap-2"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              Download Receipt
            </button>
          </div>
        </div>

        {/* Next Steps */}
        <div className="bg-blue-50 rounded-lg shadow-lg p-8">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">What's Next?</h2>
          <p className="text-gray-700 mb-4">
            Your organization is now set up with a subscription. You have 5 free disciplemaker licenses to start.
          </p>
          {checkoutSession?.subscriptionType === 'platform' && (
            <p className="text-gray-600 mb-4 text-sm bg-white rounded-lg p-3 border border-blue-200">
              <strong>Need more than 5 disciplemakers?</strong> You can add a Disciplemaker Pack from your Subscription page — your card on file will be used, no extra checkout needed.
            </p>
          )}
          <div className="flex gap-4">
            <button
              onClick={handleSetupCommunity}
              className="flex-1 bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition font-medium"
            >
              Set Up Your Community
            </button>
            <button
              onClick={() => navigate(`/organization/${orgId}/subscription`)}
              className="flex-1 bg-gray-200 text-gray-700 px-6 py-3 rounded-lg hover:bg-gray-300 transition font-medium"
            >
              View Subscription
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SubscriptionSuccess;
